import React, { useState, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import Autosuggest from 'react-autosuggest';
import algoliasearch from 'algoliasearch/lite';
import { navigate } from 'gatsby';
import DataContext from '../context/data-context';

const client = algoliasearch(
  process.env.GATSBY_ALGOLIA_CLIENT_KEY,
  process.env.GATSBY_ALGOLIA_SEARCH_KEY
);
const index = client.initIndex(process.env.GATSBY_ALGOLIA_INDEX || 'continia.docs-dev');

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;

const NavigationFilter = ({ product }) => {
  const {
    settings: { dictionary, prefix },
  } = useContext(DataContext);
  const [filterValue, setFilterValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const debounceTimerRef = useRef(null);

  const onSuggestionsFetchRequested = ({ value }) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (!value || value.trim().length < MIN_QUERY_LENGTH) {
      setSuggestions([]);
      return;
    }

    debounceTimerRef.current = setTimeout(() => {
      index
        .search(value, {
          filters: `${
            product !== 'all' ? `product:"${product}" AND ` : ''
          }language:"${prefix}"`,
          facetingAfterDistinct: true,
          queryType: 'prefixAll',
          typoTolerance: true,
          ignorePlurals: true,
        })
        .then(({ hits }) => {
          if (value.trim()) {
            setSuggestions([...hits, { isSearchAll: true, query: value }]);
          } else {
            setSuggestions(hits);
          }
        })
        .catch((err) => {
          console.log('///SEARCH ERROR', err);
          if (value.trim()) {
            setSuggestions([{ isSearchAll: true, query: value }]);
          } else {
            setSuggestions([]);
          }
        });
    }, DEBOUNCE_MS);
  };

  // Autosuggest will call this function every time you need to clear suggestions.
  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
  };

  const onSuggestionSelected = (
    event,
    { suggestion, suggestionValue, suggestionIndex, sectionIndex, method } // eslint-disable-line
  ) => {
    if (suggestion.isSearchAll) {
      navigate(`/search?q=${encodeURIComponent(suggestion.query)}`);
    } else {
      navigate(suggestion.url.substr(6));
    }
    setFilterValue('');
  };

  return (
    <div className="combobox">
      <i className="icon icon-filter" />
      <Autosuggest
        suggestions={suggestions}
        alwaysRenderSuggestions={false}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        onSuggestionSelected={onSuggestionSelected}
        getSuggestionValue={(item) => item.isSearchAll ? item.query : item.title}
        renderSuggestion={(item) => {
          if (item.isSearchAll) {
            return (
              <div className="search-all-link">
                <div className="search-all-button">
                  <span>
                    Search for "{item.query}" in all Continia Docs documentation
                  </span>
                </div>
              </div>
            );
          }
          return (
            <div className="suggestion">
              <div className="title">{item.title}</div>
              <div className="bc">
                <ul>
                  {item.breadcrumb &&
                    item.breadcrumb.map((bc) => (
                      <li key={bc.url.substr(6)}>{bc.title}</li>
                    ))}
                </ul>
              </div>
            </div>
          );
        }}
        inputProps={{
          placeholder: dictionary.filterPlaceholder,
          value: filterValue,
          className: 'input autocomplete is-small has-icon-left',
          onChange: (event, { newValue }) => {
            setFilterValue(newValue);
          },
          role: 'combobox',
          'aria-autocomplete': 'list',
          autoCapitalize: 'off',
          autoComplete: 'off',
          autoCorrect: 'off',
          spellCheck: 'false',
          'aria-expanded': 'false',
          'aria-activedescendant': '',
        }}
      />
    </div>
  );
};

NavigationFilter.propTypes = {
  product: PropTypes.string.isRequired,
};
NavigationFilter.defaultProps = {};

export default NavigationFilter;
